// Publieke afmeldpagina (/afmelden, recovery-ronde 20-08).
//
// De klant komt hier via de afmeldregel onderin de winkelwagen-
// herinnering. Geen inlog: het opake rij-id in ?token= is de
// autorisatie. Afmelden gebeurt bewust pas na een klik op de knop
// (POST) — mailscanners die links voor-openen mogen niemand afmelden.
// Opzet en palet volgen de andere publieke shop-pagina's
// (retour/tevredenheid).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { Button } from "@/components/ui/button";
import { recoveryPubliekApi } from "@/lib/api";
import {
  RetourFooter,
  RetourHeader,
  RETOUR_KOLOM,
} from "../Retour/RetourShopElementen";

const GROEN = "#2E9E5B";

type Weergave = "vraag" | "klaar" | "ongeldig";

export function AfmeldenPagina() {
  const { t } = useTranslation();
  const [zoekParams] = useSearchParams();
  const token = zoekParams.get("token") ?? "";

  const [weergave, setWeergave] = useState<Weergave>(() =>
    token ? "vraag" : "ongeldig",
  );
  const [bezig, setBezig] = useState(false);

  const meldAf = () => {
    if (bezig) return;
    setBezig(true);
    recoveryPubliekApi
      .afmelden(token)
      .then((antwoord) =>
        setWeergave(antwoord.gelukt ? "klaar" : "ongeldig"),
      )
      .catch(() => setWeergave("ongeldig"))
      .finally(() => setBezig(false));
  };

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <main className="flex-1 bg-gray-50 py-10">
        <div className={`${RETOUR_KOLOM} max-w-[640px]`}>
          {weergave === "ongeldig" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
              <h1 className="text-[22px] font-bold text-gray-900">
                {t("afmelden.ongeldig_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {t("afmelden.ongeldig_uitleg")}
              </p>
            </div>
          )}

          {weergave === "klaar" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
              <span
                className="mx-auto grid h-12 w-12 place-items-center rounded-full"
                style={{ background: "#e8f5ee" }}
              >
                <Check className="h-6 w-6" style={{ color: GROEN }} />
              </span>
              <h1 className="mt-4 text-[22px] font-bold text-gray-900">
                {t("afmelden.klaar_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {t("afmelden.klaar_uitleg")}
              </p>
            </div>
          )}

          {weergave === "vraag" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8">
              <h1 className="text-[22px] font-bold text-gray-900">
                {t("afmelden.vraag_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {t("afmelden.vraag_uitleg")}
              </p>
              <Button
                type="button"
                disabled={bezig}
                onClick={meldAf}
                data-testid="afmelden-bevestig"
                className="mt-5 h-11 w-full text-[15px]"
              >
                {bezig ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("afmelden.bevestig_knop")
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
      <RetourFooter />
    </div>
  );
}
